// Coordinate-plane geometry in mathematical units (Senior Phase primitives
// contract §5). Nothing here knows about pixels: the screen converts a tap to
// plane coordinates, and everything kept, compared or saved is mathematical.

export const MODES = ["plot_point", "plot_points", "read_point", "translate", "reflect", "rotate"];
export const TRANSFORMS = ["translate", "reflect", "rotate"];
export const FIGURE_KINDS = ["polyline", "polygon"];

// Floating-point noise from snapping (0.1 + 0.2) is not a coordinate
// difference anyone authored.
const EPSILON = 1e-9;

// The nearest multiple of `snap` (counted from zero, so -3 stays -3).
export const snapTo = (value, snap) => {
  const snapped = Math.round(value / snap) * snap;
  const decimals = (String(snap).split(".")[1] || "").length;
  return Number(snapped.toFixed(Math.min(10, decimals)));
};

export const clampTo = (value, { min, max }) => Math.min(max, Math.max(min, value));

// A tap anywhere becomes the nearest snapped coordinate on the plane.
export const placeOn = (plane, x, y) => ({
  x: clampTo(snapTo(x, plane.snap), plane.x),
  y: clampTo(snapTo(y, plane.snap), plane.y),
});

export const inRange = (plane, { x, y }) =>
  x >= plane.x.min - EPSILON &&
  x <= plane.x.max + EPSILON &&
  y >= plane.y.min - EPSILON &&
  y <= plane.y.max + EPSILON;

// Whether a learner can actually reach this coordinate: it is on the snap
// grid, or within the authored tolerance of it.
export const reachable = (plane, { x, y }, tolerance = 0) =>
  Math.abs(snapTo(x, plane.snap) - x) <= tolerance + EPSILON &&
  Math.abs(snapTo(y, plane.snap) - y) <= tolerance + EPSILON;

export const samePoint = (a, b, tolerance = 0) =>
  Boolean(a && b) &&
  Math.abs(a.x - b.x) <= tolerance + EPSILON &&
  Math.abs(a.y - b.y) <= tolerance + EPSILON;

// What the learner places, by stable id: each target point, and each vertex
// of each target figure. `shape` is the expected geometry or the step's
// answer-free `targets`; only ids, labels and topology are read to build the
// list, so either works and no coordinate is required. Keys are "point:<id>"
// and "figure:<id>:<vertex>" so a point and a vertex may share an id without
// colliding.
export const targetsOf = (shape) => [
  ...shape.points.map((point) => ({ key: `point:${point.id}`, point })),
  ...shape.figures.flatMap((figure) =>
    figure.vertices.map((vertex) => ({
      key: `figure:${figure.id}:${vertex.id}`,
      figure,
      point: vertex,
    }))
  ),
];

// The canonical response from placed coordinates: only learner-produced
// target geometry, in the authored id order.
export const responseOf = (expected, placed) => {
  const response = {};
  if (expected.points.length) {
    response.points = expected.points
      .filter((point) => placed[`point:${point.id}`])
      .map((point) => ({ id: point.id, ...xy(placed[`point:${point.id}`]) }));
  }
  if (expected.figures.length) {
    response.figures = expected.figures.map((figure) => ({
      id: figure.id,
      kind: figure.kind,
      vertices: figure.vertices
        .filter((vertex) => placed[`figure:${figure.id}:${vertex.id}`])
        .map((vertex) => ({
          id: vertex.id,
          ...xy(placed[`figure:${figure.id}:${vertex.id}`]),
        })),
    }));
  }
  return response;
};

const xy = ({ x, y }) => ({ x, y });

// Placed coordinates from a saved response, by the same keys. Anything that
// is not a finite coordinate of a current target is left out.
export const placedFrom = (expected, response) => {
  const placed = {};
  const keep = (key, point) => {
    if (Number.isFinite(point?.x) && Number.isFinite(point?.y)) placed[key] = xy(point);
  };
  const keys = new Set(targetsOf(expected).map((t) => t.key));
  for (const point of Array.isArray(response?.points) ? response.points : []) {
    if (keys.has(`point:${point?.id}`)) keep(`point:${point.id}`, point);
  }
  for (const figure of Array.isArray(response?.figures) ? response.figures : []) {
    for (const vertex of Array.isArray(figure?.vertices) ? figure.vertices : []) {
      const key = `figure:${figure.id}:${vertex?.id}`;
      if (keys.has(key)) keep(key, vertex);
    }
  }
  return placed;
};

// Every required point and vertex present, compared by id in plane units.
// No undeclared target can substitute for a required one.
export const geometryCorrect = (expected, response) => {
  const placed = placedFrom(expected, response);
  return targetsOf(expected).every(({ key, point }) =>
    samePoint(placed[key], point, expected.tolerance)
  );
};
